import { html } from 'lit';

import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/editors/elements/d-select-tag.js';
import '../../library/elements/d-section.js';
import '../../library/editors/components/d-edit-access.js';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { UpdateAccessCommandAccessLevelEnum } from '@trinnvis/dabih-api-client';
import type { UserForAccess } from '../../library/editors/components/user-for-access';
import type { EditAccessResult } from './edit-access-result';
import type { EditAccessInput } from './edit-access-input';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';

/**
 *
 * USAGE:
 *    d-application
 *
 * STATUS OK
 */
@customElement('edit-access-dialog')
export class EditAccessDialog extends BaseDialog<EditAccessInput, EditAccessResult> {
  @property({ type: String })
  displayName = '';
  @property({ type: Array })
  otherUserEmails: string[] = [];
  @property({ type: Object })
  editUser!: UserForAccess;
  @property({ type: String })
  originalEmail = '';
  @property({ type: Boolean })
  isOwner = false;
  @property({ type: Boolean })
  isSelf = false;

  width = 500;

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('access.done'), action: 'done', main: true },
    ];
  }

  protected get calculatedTitle(): string {
    return t('access.editAccessForName', { name: this.displayName });
  }

  renderBody() {
    return html`
      <d-edit-access
        .editUser=${this.editUser}
        .isSelf=${this.isSelf}
        .isOwner=${this.isOwner}
        .originalEmail=${this.originalEmail}
        .otherUserEmails=${this.otherUserEmails}
        @value-changed=${(
          e: CustomEvent<{ accessLevel: UpdateAccessCommandAccessLevelEnum; email: string; accessExpires: string }>,
        ) => {
          this.editUser = {
            ...this.editUser,
            accessLevel: e.detail.accessLevel,
            email: e.detail.email,
            accessExpires: e.detail.accessExpires,
          };
        }}
      ></d-edit-access>
    `;
  }

  protected fetchResult(detail: string | undefined): EditAccessResult {
    if (detail === 'done') {
      return {
        action: 'done',
        email: this.editUser.email,
        accessLevel: this.toAccessLevelEnum(this.editUser.accessLevel),
        accessExpires: this.editUser.accessExpires,
      };
    }
    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: EditAccessInput) {
    this.displayName = input.displayName;
    this.isOwner = input.isOwner;
    this.isSelf = input.isSelf;
    this.originalEmail = input.originalEmail;
    this.otherUserEmails = input.otherUserEmails;
    this.editUser = {
      ...input.editUser,
    };
  }

  private toAccessLevelEnum(accessLevel: string): UpdateAccessCommandAccessLevelEnum {
    switch (accessLevel) {
      case 'NONE':
        return UpdateAccessCommandAccessLevelEnum.None;
      case 'READONLY':
        return UpdateAccessCommandAccessLevelEnum.Readonly;
      case 'USER':
        return UpdateAccessCommandAccessLevelEnum.User;
      case 'ASSETS':
        return UpdateAccessCommandAccessLevelEnum.Assets;
      default:
        throw new Error('Illegal state (E723), invalid access level ' + accessLevel);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edit-access-dialog': EditAccessDialog;
  }
}
